import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, Flex, Icon, OptionListOption, Table } from '@gaiads/telia-react-component-library';
import { LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import EditableItem from '../../../../shared/EditableItem/EditableItem';
import { Language, getLocaleFromLanguage } from '../../../../utils/locales';

interface LocaleRowProps {
  locale: LocaleResponse;
  onLocaleSave?: (locale: LocaleResponse) => Promise<boolean>;
  onDelete?: (id: LocaleResponse['id']) => void;
  onBlur?: () => void;
  languages: Language[];
  isDefault?: boolean;
  isNew?: boolean;
}

export default function LocaleRow(props: LocaleRowProps) {
  const { locale, languages, onLocaleSave, onDelete, onBlur, isDefault, isNew } = props;

  const { t } = useTranslation('adminSettings');

  const [language, setLanguage] = useState<Language>();

  const handleLanguageChange = (option: OptionListOption) => {
    const language = languages.find(l => l.tag === option.value);
    setLanguage(language);
  };

  const handleLanguageSave = async (language?: Language): Promise<boolean> => {
    if (language && onLocaleSave) {
      const updatedLocale = {
        ...getLocaleFromLanguage(language),
        id: locale.id,
      };
      return await onLocaleSave(updatedLocale);
    } else return false;
  };

  const resetLanguage = useCallback(() => {
    const originalLanguage = languages?.find(l => l.tag === locale.language_tag);
    setLanguage(originalLanguage);
  }, [languages, locale]);

  const handleBlur = () => {
    onBlur?.();
    resetLanguage();
  };

  useEffect(() => {
    resetLanguage();
  }, [resetLanguage]);

  return (
    <Table.Row className="locale-settings__locale-row">
      <Table.Cell className="locale-settings__locale-cell">
        {!isDefault ? (
          <EditableItem>
            <EditableItem.Dropdown
              showSave
              onSave={() => handleLanguageSave(language)}
              onBlur={handleBlur}
              edit={isNew}
              dropdownProps={{
                placeholder: t('localeSettings.placeholders.name'),
                options: languages
                  .filter(language => language.tag.length === 5)
                  .map(language => ({
                    value: language.tag,
                    label: language.name,
                  })),
                selectedOption: language && {
                  value: language.tag,
                  label: language.name,
                },
                onChange: (event, option) => handleLanguageChange(option),
                'data-testid': `locale-${isNew ? 'new' : locale.id}-dropdown`,
              }}
              iconsFlexProps={{
                margin: { left: 'sm' },
              }}
              saveIconProps={{
                'data-testid': `locale-${isNew ? 'new' : locale.id}-save`,
              }}
            />
          </EditableItem>
        ) : (
          <BodyText>{t('localeSettings.defaultLocale', { languageName: language?.name })}</BodyText>
        )}
      </Table.Cell>
      <Table.Cell className="locale-settings__locale-cell">
        <BodyText>{language?.native_name}</BodyText>
      </Table.Cell>
      <Table.Cell className="locale-settings__locale-cell">
        <BodyText>{language?.tag}</BodyText>
      </Table.Cell>
      <Table.Cell className="locale-settings__locale-cell">
        <Flex>
          <Icon
            data-testid={`locale-${locale.id}-delete`}
            title={t('localeSettings.icons.delete')}
            name="delete"
            size="sm"
            margin={{ size: 'xs' }}
            color={isDefault ? 'gray300' : undefined}
            onClick={() => onDelete?.(locale.id)}
            clickableAppearance
          />
        </Flex>
      </Table.Cell>
    </Table.Row>
  );
}
